"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { LanguageSwitcher } from "@/components/language-switcher";
import { SubscriptionForm } from "@/components/subscription-form";
import { Footer } from "@/components/footer";
import { HeroSection } from "@/components/landing/hero-section";
import { FeaturesSection } from "@/components/landing/features-section";
import { TrustSection } from "@/components/landing/trust-section";
import { ProductSection } from "@/components/landing/product-section";
import { CTASection } from "@/components/landing/cta-section";
import { PricingSection } from "@/components/landing/pricing-section";
import { FAQSection } from "@/components/landing/faq-section";
import { useAuth } from "@/hooks/use-auth";
import { useRouter } from "@/i18n/routing";
import { useTheme } from "next-themes";

import { useTranslations } from "next-intl";
import { FEATURE_FLAGS } from "@/config/feature-flags";

export default function Home() {
  const { isAuthenticated, isLoading } = useAuth();
  const router = useRouter();
  const { setTheme } = useTheme();
  const t = useTranslations("Home");

  // State for mobile CTA bar
  const [showMobileCTABar, setShowMobileCTABar] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Set page title (runs only once)
  useEffect(() => {
    document.title = "Adrian & Anita Online - Aprende Salsa con los Campeones";
  }, []);

  // Force dark theme
  useEffect(() => {
    setTheme("dark");
  }, [setTheme]);

  // Scroll animations fallback for browsers without scroll-timeline support
  useEffect(() => {
    const handleScroll = () => {
      const elements = document.querySelectorAll(".scroll-fade-in");
      elements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight * 0.8;
        if (isVisible) {
          element.classList.add("visible");
        }
      });

      // Show/hide mobile CTA bar based on scroll direction and position
      const scrollY = window.scrollY;
      const heroHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const windowHeight = window.innerHeight;
      const footerOffset = 200; // Approximate footer height + padding

      // Check if we're near the footer
      const isNearFooter =
        scrollY + windowHeight >= documentHeight - footerOffset;

      // Check scroll direction
      const isScrollingDown = scrollY > lastScrollY;
      const isScrollingUp = scrollY < lastScrollY;

      // Show bar if:
      // 1. We've scrolled past hero section
      // 2. We're scrolling down
      // 3. We're not near the footer
      const shouldShow =
        scrollY > heroHeight * 0.3 && isScrollingDown && !isNearFooter;

      // Hide bar if:
      // 1. We're scrolling up
      // 2. We're at the top
      // 3. We're near the footer
      const shouldHide =
        isScrollingUp || scrollY < heroHeight * 0.1 || isNearFooter;

      if (shouldShow) {
        setShowMobileCTABar(true);
      } else if (shouldHide) {
        setShowMobileCTABar(false);
      }

      setLastScrollY(scrollY);
    };

    // Check on mount and scroll
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  // Redirect authenticated users to course page
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      router.push("/auth/course");
    }
  }, [isAuthenticated, isLoading, router]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  // Smooth scroll to pricing section
  const scrollToPricing = () => {
    const pricingSection = document.getElementById("pricing");
    pricingSection?.scrollIntoView({ behavior: "smooth" });
  };

  // Smooth scroll to leads section
  const scrollToLeads = () => {
    const leadsSection = document.getElementById("leads");
    leadsSection?.scrollIntoView({ behavior: "smooth" });
  };

  // Only show home page content for unauthenticated users
  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <Image
                src="/images/logo-aa-bianco-300x.png"
                alt="Adrian & Anita Online"
                width={120}
                height={40}
                className="h-8 w-auto"
                priority
              />
            </div>

            {/* Navigation */}
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <button
                onClick={() => router.push("/login")}
                className="text-sm text-gray-300 hover:text-white transition-colors"
              >
                {t("auth.login")}
              </button>
              {/* CTAs - Hidden on mobile, shown on desktop */}
              {FEATURE_FLAGS.FREE_CLASS_ENABLED && (
                <button
                  onClick={scrollToLeads}
                  className="border-2 border-white/30 hover:border-white/50 hover:bg-white/10 text-white text-sm font-medium px-6 py-2 transition-all duration-300 hover:scale-105 hidden md:block"
                >
                  {t("cta.secondaryButton")}
                </button>
              )}
              <button
                onClick={scrollToPricing}
                className="bg-[#E91E63] hover:bg-[#D81B60] text-white text-sm font-bold px-6 py-2 transition-all duration-300 hover:scale-105 hidden md:block"
              >
                {t("hero.cta")}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero Section */}
        <HeroSection
          onScrollToPricing={scrollToPricing}
          onScrollToLeads={scrollToLeads}
          showFreeClass={FEATURE_FLAGS.FREE_CLASS_ENABLED}
        />

        {/* Features & Benefits Section */}
        <FeaturesSection />

        {/* Trust & Authority Section */}
        <TrustSection />

        {/* Product & Method Section */}
        <ProductSection onScrollToPricing={scrollToPricing} />

        {/* Pricing Section */}
        <PricingSection />

        {/* Free Class Lead Magnet Section */}
        {FEATURE_FLAGS.FREE_CLASS_ENABLED && (
          <section
            id="leads"
            className="py-24 px-4 sm:px-8 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900/30 to-black"></div>
            <div className="relative z-10 max-w-2xl mx-auto scroll-fade-in">
              <SubscriptionForm />
            </div>
          </section>
        )}

        {/* FAQ Section */}
        <FAQSection />

        {/* CTA Section */}
        <CTASection
          onScrollToPricing={scrollToPricing}
          onScrollToLeads={scrollToLeads}
          showFreeClass={FEATURE_FLAGS.FREE_CLASS_ENABLED}
        />
      </main>

      {/* Mobile Bottom CTA Bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-40 bg-black/95 backdrop-blur-sm border-t border-white/10 p-4 transition-transform duration-300 md:hidden ${
          showMobileCTABar ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="flex gap-3">
          {FEATURE_FLAGS.FREE_CLASS_ENABLED && (
            <button
              onClick={scrollToLeads}
              className="flex-1 border-2 border-white/30 hover:border-white/50 hover:bg-white/10 text-white text-sm font-medium px-4 py-3 transition-all duration-300"
            >
              {t("cta.secondaryButton")}
            </button>
          )}
          <button
            onClick={scrollToPricing}
            className="flex-1 bg-[#E91E63] hover:bg-[#D81B60] text-white text-sm font-bold px-4 py-3 transition-all duration-300"
          >
            {t("hero.cta")}
          </button>
        </div>
      </div>

      <Footer />

      <style jsx global>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out;
        }

        .scroll-fade-in {
          opacity: 0;
          transform: translateY(30px);
          transition:
            opacity 0.8s ease-out,
            transform 0.8s ease-out;
        }

        .scroll-fade-in.visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* For browsers that support scroll-timeline */
        @supports (animation-timeline: view()) {
          .scroll-fade-in {
            animation: fade-in-up linear forwards;
            animation-timeline: view();
            animation-range: entry 0% cover 30%;
          }
        }
      `}</style>
    </div>
  );
}
